/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {useEffect, useState} from "react";
import {InnerVerticalTabs} from "../../../../components/Tabs/InnerVerticalTabs/InnerVerticalTabs";
import Content from "./Content";
import {BooleanParam, StringParam, useQueryParam, withDefault} from "use-query-params";
import {ArgosFavoriteIconLogic} from "../../../../components/Buttons/ArgosFavoriteIconLogic";
import {FAVORITE_PARAM_ID, FAVORITE_TYPE, INNERVTAB_URL_PARAM} from "../constants";
import {Lidar} from "../../constants";
import {ObjectDef} from "../../../objectApp/constants";


export const ViewPublicConfigsParam = withDefault(BooleanParam, false)

interface LidarPerformanceProps {
  sensor: Lidar
  objects: ObjectDef[]
  gmid: string
  onSaveConfiguration: any
  onDeleteConfiguration: any
}

const LidarPerformance = (props: LidarPerformanceProps) => {

  const [tabs, setTabs] = useState<any>([])
  const [viewPublicConfigurations, setViewPublicConfigurations] = useQueryParam('viewPublicConfigs', ViewPublicConfigsParam)
  const [_index, fff] = useQueryParam(FAVORITE_PARAM_ID, StringParam)

  function getBaselineObject(tempToObjMap: any) {
    const tempValue = Object.keys(tempToObjMap)[0]; // Get the first temperature value as default
    const _newObjectName = tempToObjMap?.[tempValue]?.["objectName"];
    const pointsFrames = tempToObjMap?.[tempValue]?.["pointsFrames"];
    const version = tempToObjMap?.[tempValue]?.["objectVersion"];
    const newObj = {
      "objectCategory": tempToObjMap?.[tempValue]?.["objectCategory"],
      "objectName": _newObjectName,
      "pointsFrames": pointsFrames,
      "objectVersion": version,
      objectVariant: tempToObjMap?.[tempValue]?.["objectVariant"]
    };
    return newObj;
  }


  const getTemperatureToObjectMapping = () => {
    const sensorData: any = props.sensor?.plotData;
    const baselineData = props.sensor?.baseline;
    const tempToObjMap1: any = {};
    const tempToObjMap2: any = {};

    Object.keys(sensorData)?.forEach((detectionTable: any) => {
      if (Object.keys(sensorData[detectionTable]).length !== 0) {
        const tableData = sensorData?.[detectionTable]?.data;
        const tempKey: any = Object.keys(tableData)?.filter((key: any) => key.includes("temperature") && key.includes("value"))[0];
        const pointsFramesKey: any = Object.keys(tableData)?.filter((key: any) => key.includes("points_frames") && key.includes("value"))[0];

        const tempValueAsString: string = String(tableData[tempKey]);

        if (detectionTable === "Detection_Range_1" || detectionTable === "Detection_Range_2") {
          tempToObjMap1[tempValueAsString] = {
            "pointsFrames": tableData[pointsFramesKey],
            "objectName": baselineData?.[0]?.objectName,
            "objectCategory": baselineData?.[0]?.objectCategory,
            "objectVariant": baselineData?.[0]?.objectVariant,
            objectVersion: baselineData?.[0]?.objectVersion
          };
        } else if (detectionTable === "Detection_Range_3" || detectionTable === "Detection_Range_4") {
          tempToObjMap2[tempValueAsString] = {
            "pointsFrames": tableData[pointsFramesKey],
            "objectName": baselineData?.[1]?.objectName,
            "objectCategory": baselineData?.[1]?.objectCategory,
            "objectVariant": baselineData?.[1]?.objectVariant,
            objectVersion: baselineData?.[1]?.objectVersion
          };
        }
      }
    });
    return {tempToObjMap1, tempToObjMap2};
  };


  useEffect(() => {
    const objectData = props.objects;
    const sensorLidarData = props.sensor;
    const sensorLidarConfigs = sensorLidarData.configurations;

    const {tempToObjMap1, tempToObjMap2} = getTemperatureToObjectMapping();

    const baselineObject1 = getBaselineObject(tempToObjMap1)
    const baselineObject2 = getBaselineObject(tempToObjMap2);

    sensorLidarData["pointsFrames"] = baselineObject1.pointsFrames;

    const baselineData1 = {
      "sensorId": sensorLidarData.id,
      "objectCategory": baselineObject1?.objectCategory,
      "selectedObject": baselineObject1?.objectName,
      "selectedPointsFrames": baselineObject1?.pointsFrames,
      "objectVersion": baselineObject1.objectVersion,
      objectVariant: baselineObject1?.objectVariant,
      'temperature': Object.keys(tempToObjMap1)[0],
      'azimuthFOV': sensorLidarData.azimuthFOV,
      'maxInstrumentedRange': sensorLidarData.maxInstrumentedRange,
      'isPublicConfig': true,
    };

    const baselineData2 = {
      "sensorId": sensorLidarData.id,
      "objectCategory": baselineObject2?.objectCategory,
      "selectedObject": baselineObject2?.objectName,
      "selectedPointsFrames": baselineObject2?.pointsFrames,
      "objectVersion": baselineObject2.objectVersion,
      objectVariant: baselineObject2?.objectVariant,
      'temperature': Object.keys(tempToObjMap2)[0],
      'azimuthFOV': sensorLidarData.azimuthFOV,
      'maxInstrumentedRange': sensorLidarData.maxInstrumentedRange,
      'isPublicConfig': true,
    };

    let localTabs: any[] = [];

    // Baseline configuration
    const baselineTab1 = {
      "trackedItemUid": 'baseline1',
      "name": "Baseline 1",
      "content": <Content configIndex={0} baselineData={baselineData1} objectData={objectData}
                          tempToObjMap={tempToObjMap1}
                          sensorLidarData={sensorLidarData} onSaveConfiguration={props.onSaveConfiguration}
                          onDeleteConfiguration={props.onDeleteConfiguration} gmid={props.gmid}/>
    };
    const baselineTab2 = {
      "trackedItemUid": 'baseline2',
      "name": "Baseline 2",
      "content": <Content configIndex={1} baselineData={baselineData2} objectData={objectData}
                          tempToObjMap={tempToObjMap2}
                          sensorLidarData={sensorLidarData} onSaveConfiguration={props.onSaveConfiguration}
                          onDeleteConfiguration={props.onDeleteConfiguration} gmid={props.gmid}/>
    };
    localTabs.push(baselineTab1, baselineTab2);

    // Owner configurations
    const ownerConfigTabs = sensorLidarConfigs?.filter((x: object) => x["owner" as keyof object] == props.gmid);
    const configTabs = ownerConfigTabs?.map((singleConfig: any, index: number) => ({
      "trackedItemUid": singleConfig['trackedItemUid'] ?? `item-${index}-${singleConfig['configName']}`,
      "name": singleConfig['configName'],
      "content": <Content configIndex={index + 2} baselineData={singleConfig} objectData={objectData}
                          tempToObjMap={tempToObjMap1}
                          sensorLidarData={sensorLidarData}
                          onSaveConfiguration={props.onSaveConfiguration}
                          onDeleteConfiguration={props.onDeleteConfiguration} gmid={props.gmid}/>
    }));
    if (configTabs != null) {
      localTabs = localTabs.concat(configTabs);
    }

    // Public configurations
    if (viewPublicConfigurations) {
      const publicConfigs = sensorLidarConfigs?.filter((x: object) => x["isPublicConfig" as keyof object] && x["owner" as keyof object] != props.gmid);
      const publicConfigTabs = publicConfigs?.map((singleConfig: any, index: number) => ({
        "trackedItemUid": singleConfig['trackedItemUid'] ?? `public-item-${index}-${singleConfig['configName']}`,
        "name": singleConfig['configName'],
        "content": <Content configIndex={index + 2} baselineData={singleConfig} objectData={objectData}
                            tempToObjMap={tempToObjMap1}
                            sensorLidarData={sensorLidarData}
                            onSaveConfiguration={props.onSaveConfiguration}
                            onDeleteConfiguration={props.onDeleteConfiguration} gmid={props.gmid}/>
      }));
      if (publicConfigTabs != null) {
        localTabs = localTabs.concat(publicConfigTabs);
      }
    }

    setTabs(localTabs);
  }, [props.sensor, props.objects, viewPublicConfigurations, props.gmid, props.onDeleteConfiguration, props.onSaveConfiguration]);


  return (
    <div style={{flexGrow: 1, display: 'flex', height: '100%', flexDirection: "column"}}>
      <div style={{display: 'flex', width: '100%'}}>
        <div style={{marginLeft: 'auto'}}>
          <ArgosFavoriteIconLogic favoriteType={FAVORITE_TYPE} trackedItemUid={props.sensor?.id} partitionKey={props.sensor?.id}/>
        </div>
      </div>
      <InnerVerticalTabs tabs={tabs} favoriteType={FAVORITE_TYPE} uid={INNERVTAB_URL_PARAM}
        // onTabSelected={(index: number) => setSelectedTab(index)}
      />
    </div>
  )
}
export default LidarPerformance;